import {
  useEffect,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  Box,
  IconButton,
  Stack,
  Text
} from '@chakra-ui/react'
import { ArrowIcon } from 'assets/icons'
import {
  getComments,
  getNotificationMessage,
  getPosts,
  setBlogParent,
  setCloseAllDrawers,
  setComments,
  setConnReqData,
  setEngagementItems,
  setNotificationSectionPage,
  setPosts,
  setSavedEvents,
  setSelectedEngagementType,
  usersSelectors
} from 'store'
import { clearXmppPushNotification } from 'store/chat/slice'
import {
  NotificationSections,
  PENDING,
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import {
  isAuth,
  isNotMobile
} from 'utils/helpers'
import {
  AnimatePresence,
  motion
} from 'framer-motion'
import { COLORS } from 'utils/styles'

import { Notifications } from 'components/Notifications'
import { GuestNotification } from '../GuestNotifications'

const MotionBox = motion(Box)

export const NotificationPanel = ({
  setShowNotifications,
  showNotifications,
  runClosingAnimation,
  clearOptions
}) => {
  const {
    blogParent,
    chatSectionPage,
    closeAllDrawers,
    getAdminNotificationsStatus,
    getCommentsStatus,
    hideNotification,
    notificationSectionPage,
    openPopupBlogs,
    openPopupEvents
  } = useSelector(usersSelectors)

  const dispatch = useDispatch()
  const [hidePanelAnimation, setHidePanelAnimation] = useState(false)
  const [runHidePanel, setRunHidePanel] = useState(false)
  let touchStart
  let touchEnd

  const closeDrawer = () => {
    dispatch(setNotificationSectionPage('clear'))
    clearOptions(false)
    localStorage.setItem('isBlockedNotification', 'false')
    localStorage.setItem('isNotifOpen', 'false')
  }

  if (localStorage.getItem('is-notif-modal-open') === null) {
    localStorage.setItem('is-notif-modal-open', 'false')
  }

  const animateVariants = {
    initial: { x: 500 },
    animate: {
      x: runHidePanel || runClosingAnimation ? 500 : 0,
      transition: {
        type: hidePanelAnimation || runClosingAnimation ? 'tween' : 'spring',
        duration: 0.8
      }
    },
    exit: {
      x: 500,
      transition: {
        type: 'tween',
        duration: 0.8
      }
    },
    exit0: {
      x: 500,
      transition: {
        type: 'tween',
        duration: 0
      }
    }
  }

  const handleContent = (pageData) => (
    <MotionBox
      id={`${pageData.key}-panel`}
      key={pageData.key}
      variants={animateVariants}
      initial="initial"
      animate="animate"
      exit={
        (
          notificationSectionPage?.at(-1)?.key === pageData?.key
          && isNotMobile()
        )
        || (
          notificationSectionPage?.at(-1)?.key === pageData?.key
          && !isNotMobile()
          && !openPopupBlogs
          && !openPopupEvents
          && !chatSectionPage?.length
        )
          ? 'exit'
          : 'exit0'
      }
      width="95% !important"
      borderRadius="10px"
      marginBlock="60px"
      marginInline="10px"
      overflow="hidden"
      background={
        pageData.key === NotificationSections.CONNECTION_REQUEST
          ? COLORS.white
          : '#c4c3c7'
      }
      maxWidth="450px"
      position="fixed"
      top="env(safe-area-inset-top)"
      right={0}
      height="calc(100dvh - 120px)"
      maxHeight="calc(100dvh - 120px - env(safe-area-inset-top))"
      display="flex"
      flexDirection="column"
      zIndex={1405}
      boxShadow="0 4px 10px rgba(0, 0, 0, 0.2) !important"
      sx={{
        '::-webkit-scrollbar': {
          display: 'none'
        },
        '-ms-overflow-style': 'none',
        'scrollbar-width': 'none'
      }}
    >
      <Stack
        id={`${pageData.key}-panel-header`}
        flex="0 0 48px"
        flexShrink={0}
        spacing="43px"
        alignItems="center"
        justifyContent="space-between"
        direction="row"
        p="5px 0"
        background={COLORS.goldGradientRightToLeft}
        height="48px"
        onTouchStart={(e) => {
          touchStart = e?.targetTouches[0].clientX
        }}
        onTouchMove={(e) => {
          touchEnd = e?.targetTouches[0].clientX
        }}
        onTouchEnd={() => {
          if (touchStart - touchEnd < -45) {
            closeDrawer()
          }
        }}
      >
        <div />
        <Text
          whiteSpace="nowrap"
          fontSize="24px"
          textOverflow="ellipsis"
          overflow="hidden"
          fontWeight={600}
          paddingLeft="50px"
        >
          {pageData.title}
        </Text>

        <IconButton
          _hover={{ background: COLORS.gold_hover }}
          background="unset"
          transform="rotate(180deg)"
          style={{
            marginRight: '5px',
            borderRadius: '5px',
            height: '40px'
          }}
          icon={<ArrowIcon />}
          onClick={() => {
            if (
              notificationSectionPage.at(-1)?.key === NotificationSections.BLOG_COMMENTS_FOR_POST
              && blogParent.length > 1
            ) {
              if (getCommentsStatus === PENDING) {
                dispatch(getNotificationMessage({
                  type: REJECTED,
                  message: 'Please finish loading first before returning to previous blog post or comment',
                  isEditProfile: false
                }))
              } else {
                const parentData = blogParent[blogParent.length - 1]

                dispatch(setComments([]))
                dispatch(getComments({
                  type: parentData.parent_type,
                  ...(parentData.parent_type === 'post' ? { post_id: parentData.post_id } : { comment_id: parentData.parent_comment_id })
                }))

                dispatch(setBlogParent(blogParent.slice(0, -1)))
              }
            } else if (
              notificationSectionPage.at(-1)?.key === NotificationSections.BLOG_COMMENTS_FOR_POST
              && blogParent.length === 1
            ) {
              const parent = blogParent[0]
              dispatch(setPosts([]))

              let data = {}
              if (parent.type === 'friends' || parent.type === 'all') {
                data = {
                  type: parent.type
                }
              } else if (parent.type === 'affinity') {
                data = {
                  type: parent.type,
                  id: blogParent[0].affinities[0].id
                }
              } else if (parent.type === 'profile' || parent.type === 'subscribed') {
                data = {
                  type: parent.type,
                  id: blogParent[0].profile_id
                }
              }

              dispatch(getPosts(data))
              dispatch(setNotificationSectionPage(null))
            } else if (
              notificationSectionPage.at(-1)?.key === NotificationSections.NOTIFICATIONS
            ) {
              closeDrawer()
            } else if (
              notificationSectionPage.at(-1)?.key === NotificationSections.ENGAGEMENT_ITEM_LIST
            ) {
              dispatch(setNotificationSectionPage(null))

              setTimeout(() => {
                dispatch(setEngagementItems([]))
                dispatch(setSelectedEngagementType(''))
              }, 800)
            } else if (
              notificationSectionPage.at(-1)?.key === NotificationSections.SAVED_EVENTS_LIST
            ) {
              dispatch(setNotificationSectionPage(null))
              setTimeout(() => dispatch(setSavedEvents([])), 800)
            } else if (
              notificationSectionPage.at(-1)?.key === NotificationSections.CONNECTION_REQUEST
            ) {
              dispatch(setConnReqData([]))
              dispatch(setNotificationSectionPage(null))
            } else {
              dispatch(setNotificationSectionPage(null))
            }
          }}
          id="back-button"
          outline="none"
          boxShadow="none"
          height="32px"
          borderRadius="0"
          css={{
            ':focus': {
              outline: 'none',
              boxShadow: 'none'
            }
          }}
        />
      </Stack>

      <Box
        flex="1 1 auto"
        overflowY="auto"
        sx={{
          '::-webkit-scrollbar': { display: 'none' },
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none'
        }}
        background={
          pageData.key === NotificationSections.CONNECTION_REQUEST
            ? COLORS.white
            : '#c4c3c7'
        }
      >
        {
          isAuth()
            ? (
              <Notifications
                pageName={pageData.key}
                setShowNotifications={setShowNotifications}
                showNotifications={showNotifications}
              />
            )
            : <GuestNotification />
        }
      </Box>
    </MotionBox>
  )

  useEffect(() => setRunHidePanel(hideNotification), [hideNotification])

  useEffect(() => {
    if (closeAllDrawers) {
      closeDrawer()
    }
  }, [closeAllDrawers])

  useEffect(() => {
    if (getAdminNotificationsStatus === SUCCEEDED) {
      dispatch(clearXmppPushNotification())
    }
  }, [getAdminNotificationsStatus])

  useEffect(() => {
    setHidePanelAnimation(true)
    setRunHidePanel(hideNotification)
    setTimeout(() => setHidePanelAnimation(false), 800)
  }, [hideNotification])

  useEffect(() => {
    const id = sessionStorage.getItem('id')

    if (id !== null) {
      dispatch(setNotificationSectionPage('clear'))

      sessionStorage.removeItem('id')
    }
  }, [])

  return (
    <AnimatePresence>
      {
        notificationSectionPage?.length
        && (
          <>
            <Box
              zIndex={isAuth() ? 1399 : 1390}
              background="transparent"
              position="fixed"
              width="100%"
              height="50px"
              top={0}
              onClick={() => {
                if (!closeAllDrawers) {
                  dispatch(setCloseAllDrawers(true))
                  setTimeout(() => dispatch(setCloseAllDrawers(false)), 850)
                }
              }}
            />

            <Box
              zIndex={isAuth() ? 1399 : 1390}
              background="transparent"
              position="fixed"
              width="100%"
              height="50px"
              bottom={0}
              onClick={() => {
                if (!closeAllDrawers) {
                  dispatch(setCloseAllDrawers(true))
                  setTimeout(() => dispatch(setCloseAllDrawers(false)), 850)
                }
              }}
            />
          </>
        )
      }

      { notificationSectionPage.map((pageData) => handleContent(pageData)) }
    </AnimatePresence>
  )
}
